/**
 * @fileoverview Utility class for handling media device operations and errors.
 * Provides functionality to check audio recording support and process media device errors.
 *
 * @example
 * // Check if device supports audio recording
 * const mediaDevice = new MediaDevice();
 * if (mediaDevice.isSupportingAudioRecording()) {
 *   // Proceed with audio recording
 * } else {
 *   // Show unsupported message
 * }
 *
 * @example
 * // Handle media device errors
 * try {
 *   // Attempt media operation
 * } catch (e) {
 *   const errorMessage = mediaDevice.processMediaDeviceError(e);
 *   console.error(errorMessage);
 * }
 */

/**
 * @enum UserMediaErrors
 * @description Enum for user media errors
 * @property {string} ABORT - The abort error
 * @property {string} NOT_ALLOWED - The not allowed error
 * @property {string} NOT_FOUND - The not found error
 * @property {string} NOT_READABLE - The not readable error
 * @property {string} OVERCONSTRAINED - The overconstrained error
 * @property {string} SECURITY - The security error
 * @property {string} TYPE - The type error
 */
enum UserMediaErrors {
    ABORT = 'AbortError',
    NOT_ALLOWED = 'NotAllowedError',
    NOT_FOUND = 'NotFoundError',
    NOT_READABLE = 'NotReadableError',
    OVERCONSTRAINED = 'OverconstrainedError',
    SECURITY = 'SecurityError',
    TYPE = 'TypeError',
}

/**
 * @class MediaDevice
 * @description Utility class for handling media device operations and errors
 */
export class MediaDevice {
    /**
     * Helper function to detect whether user's device is capable of recording audio streams
     * @returns boolean Returns a boolean that indicates if user's device is capable of recording audio streams
     */
    isSupportingAudioRecording(): boolean {
        return Boolean(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
    }

    /**
     * Helper function to process Media Device Stream errors
     * @param e Error Media Device Stream error to process
     * @returns void
     */
    processMediaDeviceError(e: Error) {
        switch (e.name) {
            case UserMediaErrors.ABORT:
                return 'Aborted by the user.'

            case UserMediaErrors.NOT_ALLOWED:
                return `Audio input device permissions has been denied: ${e.message}`

            case UserMediaErrors.NOT_FOUND:
                return `Audio input sources not found: ${e.message}`

            case UserMediaErrors.NOT_READABLE:
                return `Audio media stream is not readable: ${e.message}`

            case UserMediaErrors.OVERCONSTRAINED:
                return `Audio input device is overconstrained: ${e.message}`

            case UserMediaErrors.SECURITY:
                return `Audio input device security issue: ${e.message}`

            case UserMediaErrors.TYPE:
                return `List of audio device constrains is set to empty or all constrains are set to false: ${e.message}`

            default:
                return `An unknown error occured during the initialization process of the media stream: ${e.message}`
        }
    }

    /**
     * Helper function to get a media stream
     * @returns Promise<MediaStream> A promise that resolves to a MediaStream
     */
    async getStream(): Promise<MediaStream> {
        const devices = await navigator.mediaDevices.enumerateDevices()
        const device = devices.find((d) => d.kind === 'audioinput' && d.label.includes('Default'))
        return navigator.mediaDevices.getUserMedia({
            audio: {
                deviceId: device?.deviceId,
                channelCount: 2,
                noiseSuppression: false,
                autoGainControl: true,
                echoCancellation: false,
            },
        })
    }
}
